import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useState } from 'react';
import { TextField as MuiTextField } from '@material-ui/core';
import cn from 'classnames';
import { ChevronDown } from '../../icons';
import { useBiAction } from '../BiTracking';
import { BottomSheetContentLayout } from '../BottomSheet';
import { Checkbox } from '../Checkbox';
import { useDevice } from '../Device';
import { Row } from '../Layout';
import { useOverlayContext } from '../OverlayContext';
import { Tooltip } from '../Tooltip';
import { skinToColor, useInlineSelectStyles } from './InlineSelect.styles';
const isMultipleProps = (props)=>props.multiple === true;
const defaultRenderMultipleValue = (values, children)=>{
    const labels = [];
    React.Children.forEach(children, (child)=>{
        var _childProps_label;
        if (!/*#__PURE__*/ React.isValidElement(child)) return;
        const childProps = child.props;
        if (!values.includes(childProps.value)) return;
        if (labels.length > 0) labels.push(', ');
        labels.push(/*#__PURE__*/ _jsx(React.Fragment, {
            children: (_childProps_label = childProps.label) !== null && _childProps_label !== void 0 ? _childProps_label : childProps.children
        }, String(childProps.value)));
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: labels
    });
};
function InlineSelectBottomSheet(props) {
    const { children, value, onChange, isSelected } = props;
    const { popBottomSheetPage } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        children: React.Children.map(children, (child)=>{
            if (!/*#__PURE__*/ React.isValidElement(child)) return child;
            const childProps = child.props;
            return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, childProps), {
                selected: isSelected ? isSelected(childProps.value, value) : childProps.value === value,
                onClick: ()=>{
                    onChange === null || onChange === void 0 ? void 0 : onChange({
                        target: {
                            value: childProps.value
                        }
                    });
                    popBottomSheetPage();
                }
            }));
        })
    });
}
function decorateWithCheckboxes(children, isCheckedFor, onItemClick) {
    return React.Children.map(children, (child)=>{
        if (!/*#__PURE__*/ React.isValidElement(child)) return child;
        const childProps = child.props;
        const isChecked = isCheckedFor(childProps.value);
        return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, childProps), {
            selected: isChecked
        }), onItemClick != null ? {
            onClick: ()=>onItemClick(childProps.value)
        } : {}), {
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: 8,
                alignItems: "center",
                children: [
                    /*#__PURE__*/ _jsx(Checkbox, {
                        noBi: true,
                        checked: isChecked
                    }),
                    childProps.children
                ]
            })
        }));
    });
}
function InlineSelectMultipleBottomSheet(props) {
    const { children, initialValues, onSave, saveButtonLabel } = props;
    const { hideBottomSheet } = useOverlayContext();
    const [tempValues, setTempValues] = useState(initialValues);
    const toggle = (value)=>{
        setTempValues((prev)=>prev.includes(value) ? prev.filter((v)=>v !== value) : [
                ...prev,
                value
            ]);
    };
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        footer: {
            primaryAction: {
                text: saveButtonLabel !== null && saveButtonLabel !== void 0 ? saveButtonLabel : 'Save',
                onClick: ()=>{
                    onSave(tempValues);
                    hideBottomSheet();
                },
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- Save commits the selection; onSave -> handleMultipleSave already logs the commit as `inline_select`/`select`, so a button bi would double-fire the same gesture
                noBi: true
            },
            paddingTop: 8
        },
        children: decorateWithCheckboxes(children, (value)=>tempValues.includes(value), toggle)
    });
}
// See note on BaseTextField — forwardRef + MUI types collapses BiProps' discriminated union.
const InlineSelectImpl = /*#__PURE__*/ forwardRef(function InlineSelect(props, ref) {
    var _props_InputProps;
    const isMultiple = isMultipleProps(props);
    const { children, className, required, testId, inputProps, menuMaxHeight = 300, menuMaxWidth, skin = 'standard', SelectProps, helperText, value, onChange, placeholder, bi, // WHY: BiProps union remnant — consumer supplies bi; strip noBi from rest so it never spreads onto MuiTextField's DOM input
    noBi: _noBi } = props, rest = _object_without_properties(props, [
        "children",
        "className",
        "required",
        "testId",
        "inputProps",
        "menuMaxHeight",
        "menuMaxWidth",
        "skin",
        "SelectProps",
        "helperText",
        "value",
        "onChange",
        "placeholder",
        "bi",
        "noBi"
    ]);
    const isSelected = isMultiple ? undefined : props.isSelected;
    const renderMultipleValue = isMultiple ? props.renderValue : undefined;
    const saveButtonLabel = isMultiple ? props.saveButtonLabel : undefined;
    const logBiAction = useBiAction();
    const { isMobileDevice } = useDevice();
    const logSelectionBi = (selectedValue)=>{
        if ((bi === null || bi === void 0 ? void 0 : bi.name) == null) return;
        logBiAction({
            entityType: 'inline_select',
            entityName: bi.name,
            actionType: 'select',
            testId: testId !== null && testId !== void 0 ? testId : '',
            data: _object_spread_props(_object_spread({}, bi.data), {
                selected_value: JSON.stringify(selectedValue)
            })
        });
    };
    const handleChange = (e)=>{
        logSelectionBi(e.target.value);
        if (isMultiple) {
            const arrayValue = e.target.value;
            onChange(arrayValue);
        } else {
            onChange === null || onChange === void 0 ? void 0 : onChange(e);
        }
    };
    const handleMultipleSave = (values)=>{
        logSelectionBi(values);
        onChange(values);
    };
    const { pushBottomSheetPage } = useOverlayContext();
    const overrides = useInlineSelectStyles({
        menuMaxHeight,
        menuMaxWidth,
        color: skinToColor[skin],
        isMobileDevice
    });
    function openMobileSheet() {
        if (isMultiple) {
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(InlineSelectMultipleBottomSheet, {
                    initialValues: value,
                    onSave: handleMultipleSave,
                    saveButtonLabel: saveButtonLabel,
                    children: children
                })
            });
        } else {
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(InlineSelectBottomSheet, {
                    value: value,
                    onChange: handleChange,
                    isSelected: isSelected,
                    children: children
                })
            });
        }
    }
    const mobileSelectProps = isMobileDevice ? {
        open: false,
        onOpen: openMobileSheet
    } : {};
    const decoratedChildren = isMultiple ? decorateWithCheckboxes(children, (val)=>value.includes(val)) : children;
    const multipleRenderValue = isMultiple ? (selected)=>{
        const selectedArray = selected;
        if (renderMultipleValue != null) return renderMultipleValue(selectedArray);
        if (selectedArray.length === 0) return /*#__PURE__*/ _jsx("span", {
            className: overrides.selectPlaceholder,
            children: placeholder !== null && placeholder !== void 0 ? placeholder : ''
        });
        return defaultRenderMultipleValue(selectedArray, children);
    } : undefined;
    const textField = /*#__PURE__*/ _jsx(MuiTextField, _object_spread_props(_object_spread({
        innerRef: ref,
        value: value,
        onChange: handleChange,
        placeholder: placeholder
    }, rest), {
        "data-testid": testId,
        select: true,
        className: cn(className, overrides.root),
        variant: "standard",
        size: "small",
        helperText: undefined,
        InputProps: {
            ref: (_props_InputProps = props.InputProps) === null || _props_InputProps === void 0 ? void 0 : _props_InputProps.ref,
            required,
            classes: {
                root: overrides.inputRoot,
                input: overrides.inputInput
            }
        },
        SelectProps: _object_spread({
            IconComponent: (param)=>{
                let { className } = param;
                return /*#__PURE__*/ _jsx(ChevronDown, {
                    size: 16,
                    className: cn(className, overrides.icon)
                });
            },
            MenuProps: {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                },
                getContentAnchorEl: null,
                classes: {
                    paper: overrides.menu
                },
                ['data-testid']: testId && "".concat(testId, "-backdrop")
            },
            SelectDisplayProps: {
                ['data-testid']: testId && "".concat(testId, "-select")
            },
            multiple: isMultiple,
            displayEmpty: isMultiple ? true : undefined,
            renderValue: multipleRenderValue
        }, SelectProps, mobileSelectProps),
        inputProps: _object_spread({
            ['data-testid']: testId && "".concat(testId, "-input")
        }, inputProps),
        children: decoratedChildren
    }));
    return helperText ? /*#__PURE__*/ _jsx(Tooltip, {
        placement: "top",
        title: helperText,
        testId: testId && "".concat(testId, "-tooltip"),
        children: textField
    }) : textField;
});
export const InlineSelect = InlineSelectImpl;
